/**
 * The browser → Worker hop of a pixel event's Conversions API copy (108O).
 *
 * The browser fires an event on the pixel and posts the same event — same
 * event_id, same product fields — to /api/public/capi; the Worker sends it to
 * Meta with the store's token, adding what only it can see truthfully (the
 * buyer's IP and user agent). Meta counts the pair once. Purchase is not here:
 * the server has always sent it on settlement, keyed on the order id.
 */
import { EVENT_FIELDS, VIEW_BAG_EVENT } from "./pixel";

export const CAPI_BROWSER_EVENTS = [
  "PageView",
  "ViewContent",
  VIEW_BAG_EVENT,
  "AddToCart",
  "InitiateCheckout",
  "AddPaymentInfo",
] as const;

export type CapiBrowserEvent = (typeof CAPI_BROWSER_EVENTS)[number];

export function isCapiBrowserEvent(event: string): event is CapiBrowserEvent {
  return (CAPI_BROWSER_EVENTS as readonly string[]).includes(event);
}

/** The events where the buyer has typed who they are; only these carry form data. */
export const CAPI_FORM_EVENTS: readonly CapiBrowserEvent[] = ["InitiateCheckout", "AddPaymentInfo"];

/** The buyer as typed at checkout, raw (the Worker normalises and hashes). */
export interface CapiFormUser {
  em?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  ct?: string;
  st?: string;
  zp?: string;
  country?: string;
}

/** What the browser posts for one event. */
export interface CapiBeacon {
  px: string;
  ev: CapiBrowserEvent;
  id: string;
  url: string;
  cd?: Record<string, unknown>;
  ud?: CapiFormUser;
  fbp?: string;
  fbc?: string;
  xid?: string;
}

const MAX_LINES = 50;
const MAX_AMOUNT = 1e8;

const isText = (v: unknown): v is string => typeof v === "string" && v.length > 0 && v.length <= 100;
const isAmount = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= MAX_AMOUNT;
const isCount = (v: unknown): v is number => Number.isInteger(v) && (v as number) >= 0 && (v as number) <= 10_000;

/**
 * The event's custom_data as Meta lists it for that event — only those keys,
 * only well-formed values. The body comes from a browser, so anything else
 * (an extra key, a wrong type, a negative price) is dropped, never forwarded.
 */
export function capiCustomData(event: CapiBrowserEvent, cd: Record<string, unknown> | undefined): Record<string, unknown> {
  if (event === "PageView" || !cd) return {};
  const out: Record<string, unknown> = {};
  for (const key of EVENT_FIELDS[event]) {
    const v = cd[key];
    if (key === "content_ids" && Array.isArray(v) && v.length <= MAX_LINES && v.every(isText)) out[key] = v;
    else if (key === "content_type" && v === "product") out[key] = v;
    else if (key === "currency" && typeof v === "string" && /^[A-Z]{3}$/.test(v)) out[key] = v;
    else if (key === "value" && isAmount(v)) out[key] = v;
    else if (key === "num_items" && isCount(v)) out[key] = v;
    else if (key === "contents" && Array.isArray(v) && v.length <= MAX_LINES) {
      const lines = v.filter(
        (l): l is { id: string; quantity: number; item_price: number } =>
          !!l && typeof l === "object" && isText(l.id) && isCount(l.quantity) && isAmount(l.item_price),
      );
      if (lines.length === v.length) out[key] = lines.map((l) => ({ id: l.id, quantity: l.quantity, item_price: l.item_price }));
    }
  }
  return out;
}
